import React, { memo, useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './TodoCustomFields.css';

/**
 * TodoCustomFields Component
 * 
 * Displays custom field values inline in the TodoItem.
 * Only shows fields that are enabled in elementVisibility.
 */
const TodoCustomFields = memo(({ task, customFields, onCustomFieldValueChange, elementVisibility }) => {
  // Filter to only show visible custom fields
  const visibleFields = customFields.filter(field => elementVisibility[field.id] === true);
  
  if (visibleFields.length === 0) {
    return null;
  }

  return (
    <div className="todo-custom-fields">
      {visibleFields.map(field => (
        <TodoCustomFieldValue
          key={field.id}
          field={field}
          value={task?.customFieldValues?.[field.id]}
          taskId={task?.id}
          onValueChange={onCustomFieldValueChange}
        />
      ))}
    </div>
  );
});

TodoCustomFields.displayName = 'TodoCustomFields';

/**
 * TodoCustomFieldValue - renders a single custom field value
 */
const TodoCustomFieldValue = memo(({ field, value, taskId, onValueChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value ?? '');
  const inputRef = useRef(null);

  // Sync local value when value changes externally
  useEffect(() => {
    if (!isEditing) {
      setLocalValue(value ?? '');
    }
  }, [value, isEditing]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.select) {
        inputRef.current.select();
      }
    }
  }, [isEditing]);

  // Handle save
  const handleSave = useCallback(() => {
    if (onValueChange && taskId) {
      onValueChange(taskId, field.id, localValue);
    }
    setIsEditing(false);
  }, [taskId, field.id, localValue, onValueChange]);

  // Handle key down
  const handleKeyDown = useCallback((e) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setLocalValue(value ?? '');
      setIsEditing(false);
    }
  }, [handleSave, value]);

  // Handle checkbox toggle
  const handleCheckboxClick = useCallback((e) => {
    e.stopPropagation();
    if (onValueChange && taskId) {
      onValueChange(taskId, field.id, !value);
    }
  }, [taskId, field.id, value, onValueChange]);

  // Checkbox type
  if (field.type === 'checkbox') {
    const isChecked = value === true;
    return (
      <button
        className={`todo-custom-field todo-custom-field--checkbox ${isChecked ? 'todo-custom-field--checked' : ''}`}
        onClick={handleCheckboxClick}
        title={field.name}
      >
        <span className="todo-custom-field__checkbox">
          {isChecked && (
            <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="2,6 5,9 10,3" />
            </svg>
          )}
        </span>
        <span className="todo-custom-field__label">{field.name}</span>
      </button>
    );
  }

  // Select type - custom dropdown
  if (field.type === 'select') {
    return (
      <TodoSelectDropdown
        field={field}
        value={value}
        taskId={taskId}
        onValueChange={onValueChange}
      />
    );
  }

  // Date type
  if (field.type === 'date') {
    if (isEditing) {
      return (
        <input
          ref={inputRef}
          type="date"
          className="todo-custom-field__input"
          value={localValue || ''}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
        />
      );
    }

    const displayValue = value ? new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : null;
    return (
      <button
        className="todo-custom-field"
        onClick={(e) => {
          e.stopPropagation();
          setIsEditing(true);
        }}
        title={field.name}
      >
        {displayValue ? (
          <span className="todo-custom-field__value">{field.name}: {displayValue}</span>
        ) : (
          <span className="todo-custom-field__empty">{field.name}: —</span>
        )}
      </button>
    );
  }

  // URL type
  if (field.type === 'url') {
    if (value) {
      let displayUrl = value;
      try {
        const url = new URL(value);
        displayUrl = url.hostname.replace('www.', '');
      } catch {}
      
      return (
        <a 
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="todo-custom-field todo-custom-field--link"
          title={`${field.name}: ${value}`}
          onClick={(e) => e.stopPropagation()}
        >
          {field.name}: 🔗 {displayUrl}
        </a>
      );
    }

    if (isEditing) {
      return (
        <input
          ref={inputRef}
          type="url"
          className="todo-custom-field__input"
          value={localValue || ''}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          placeholder="https://..."
        />
      );
    }

    return (
      <button
        className="todo-custom-field"
        onClick={(e) => {
          e.stopPropagation();
          setIsEditing(true);
        }}
        title={field.name}
      >
        <span className="todo-custom-field__empty">{field.name}: —</span>
      </button>
    );
  }

  // Number type
  if (field.type === 'number') {
    if (isEditing) {
      return (
        <input
          ref={inputRef}
          type="number"
          className="todo-custom-field__input todo-custom-field__input--number"
          value={localValue ?? ''}
          onChange={(e) => setLocalValue(e.target.value === '' ? null : Number(e.target.value))}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          placeholder="0"
        />
      );
    }

    return (
      <button
        className="todo-custom-field"
        onClick={(e) => {
          e.stopPropagation();
          setIsEditing(true);
        }}
        title={field.name}
      >
        {value !== undefined && value !== null ? (
          <span className="todo-custom-field__value todo-custom-field__value--number">
            {field.name}: {typeof value === 'number' ? value.toLocaleString() : value}
          </span>
        ) : (
          <span className="todo-custom-field__empty">{field.name}: —</span>
        )}
      </button>
    );
  }

  // Text type (default)
  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        className="todo-custom-field__input"
        value={localValue || ''}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        placeholder="Enter text..."
      />
    );
  }

  return (
    <button
      className="todo-custom-field"
      onClick={(e) => {
        e.stopPropagation();
        setIsEditing(true);
      }}
      title={field.name}
    >
      {value ? (
        <span className="todo-custom-field__value">{field.name}: {value}</span>
      ) : (
        <span className="todo-custom-field__empty">{field.name}: —</span>
      )}
    </button>
  );
});

TodoCustomFieldValue.displayName = 'TodoCustomFieldValue';

/**
 * TodoSelectDropdown - Custom dropdown for select fields
 * Uses portal for proper rendering outside todo items
 */
const TodoSelectDropdown = memo(({ field, value, taskId, onValueChange }) => {
  const [showMenu, setShowMenu] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  const selectedOption = field.options?.find(opt => opt.id === value);
  const displayLabel = selectedOption?.label || 'None';
  const displayColor = selectedOption?.color || '#6b7280';

  // Calculate menu position when opening
  useEffect(() => {
    if (showMenu && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
  }, [showMenu]);

  // Close menu when clicking outside
  useEffect(() => {
    if (!showMenu) return;

    const handleClickOutside = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setShowMenu(false);
    };

    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [showMenu]);

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  }, [showMenu]);

  const handleSelect = useCallback((optionId) => {
    onValueChange?.(taskId, field.id, optionId);
    setShowMenu(false);
  }, [onValueChange, taskId, field.id]);

  const menuContent = showMenu && (
    <div 
      ref={menuRef}
      className="todo-custom-field__dropdown-menu"
      style={{ 
        position: 'fixed',
        top: menuPosition.top, 
        left: menuPosition.left,
        zIndex: 10000,
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* None option */}
      <button
        className={`todo-custom-field__dropdown-option ${!value ? 'todo-custom-field__dropdown-option--active' : ''}`}
        onClick={() => handleSelect(null)}
      >
        <span className="todo-custom-field__dropdown-dot" style={{ background: '#6b7280' }} />
        <span className="todo-custom-field__dropdown-label">None</span>
      </button>
      {/* Field options */}
      {field.options?.map(option => (
        <button
          key={option.id}
          className={`todo-custom-field__dropdown-option ${option.id === value ? 'todo-custom-field__dropdown-option--active' : ''}`}
          onClick={() => handleSelect(option.id)}
        >
          <span className="todo-custom-field__dropdown-dot" style={{ background: option.color }} />
          <span className="todo-custom-field__dropdown-label">{option.label}</span>
        </button>
      ))}
    </div>
  );

  return (
    <>
      <div className="todo-custom-field--dropdown-row">
        <span className="todo-custom-field__field-label">{field.name}</span>
        <button 
          ref={btnRef}
          className="todo-custom-field todo-custom-field--dropdown"
          onClick={handleClick}
          onMouseDown={(e) => e.stopPropagation()}
          title={field.name}
          style={{
            '--field-color': displayColor,
            '--field-bg': `${displayColor}15`,
          }}
        >
          <span className="todo-custom-field__dropdown-dot" style={{ background: displayColor }} />
          <span className="todo-custom-field__dropdown-value">
            {displayLabel}
          </span>
          <span className="todo-custom-field__dropdown-arrow">▾</span>
        </button>
      </div>
      {showMenu && createPortal(menuContent, document.body)}
    </>
  );
});

TodoSelectDropdown.displayName = 'TodoSelectDropdown';

export default TodoCustomFields;
